import { RouteAndTeamContext } from "@/components/context-providers/RouteAndTeamInfoProvider";
import {
  IMPERSONATION_COOKIE_NAME,
  subscribeToImpersonationCookie,
} from "@/utils/impersonation-utils";
import Cookies from "js-cookie";
import { SessionContext, SessionContextValue } from "next-auth/react";
import { createContext, useContext, useSyncExternalStore } from "react";

// Stands in when a test's `jest.mock("next-auth/react", ...)` factory omits `SessionContext`.
const MISSING_SESSION_CONTEXT = createContext<SessionContextValue | undefined>(undefined);
const sessionContext = SessionContext ?? MISSING_SESSION_CONTEXT;

// Allows fixture-backed components to simulate cookies without changing the browser session.
export const ImpersonationCookieContext = createContext<boolean | undefined>(undefined);

/** The cookie team, independent of the session's home team and GetTeam response. */
export function useImpersonatedTeamId(): string | undefined {
  return useSyncExternalStore(
    subscribeToImpersonationCookie,
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Client-only display state; backend authorization uses getAuthorizedTeamId.
    () => Cookies.get(IMPERSONATION_COOKIE_NAME),
    () => undefined,
  );
}

/** Cookie presence for backend authorization, independent of the viewed team. */
export function useIsImpersonating(options: { cookieOnly: true }): boolean;
export function useIsImpersonating(options?: { cookieOnly?: false }): boolean | undefined;
/**
 * Whether the signed-in user is a Chalk employee viewing another team's data, or
 * `undefined` while that cannot be decided yet.
 *
 * Only `@chalk.ai` users can impersonate (`/api/project-info` and `/api/impersonate` enforce
 * it), so everyone else is `false`. For employees the viewed team (from GetTeam, which honors
 * the impersonation cookie) is compared against the session's home team. A CLI hand-off
 * session already carries the impersonated team as its home team, so it is flagged by its
 * `impersonated` claim instead.
 *
 * The result is `undefined` for an employee on a project-scoped page whose team has not
 * resolved: GetTeam fails with PermissionDenied until `useAutoImpersonate` sets the cookie,
 * and reporting `false` in that window would attribute the page to the employee's own team.
 *
 * Safe outside `RouteAndTeamInfoProvider` (the cookie is the only signal there) and outside
 * `SessionProvider` (no session means no impersonation). Design-system components call this
 * through `useAmplitudeOnClick`, so it must not throw where `useSession` would.
 */
export function useIsImpersonating({ cookieOnly = false }: { cookieOnly?: boolean } = {}):
  | boolean
  | undefined {
  const session = useContext(sessionContext);
  const routeAndTeamInfo = useContext(RouteAndTeamContext);
  const cookieOverride = useContext(ImpersonationCookieContext);
  const cookieTeamId = useImpersonatedTeamId();
  const hasCookie = Boolean(cookieTeamId);

  // Backend authorization follows cookie presence; analytics follows the viewed team below.
  if (cookieOnly) {
    return cookieOverride ?? hasCookie;
  }

  if (session?.status !== "authenticated") {
    return false;
  }
  const isChalkEmployee = session.data.user?.email?.endsWith("@chalk.ai") ?? false;
  if (!isChalkEmployee) {
    return false;
  }
  if (session.data.impersonated === true) {
    return true;
  }

  const homeTeamId = session.data.teamId ?? undefined;
  const viewedTeamId = routeAndTeamInfo?.team?.id;
  if (viewedTeamId != null) {
    return viewedTeamId !== homeTeamId;
  }

  if (cookieTeamId) {
    return cookieTeamId !== homeTeamId;
  }

  const onScopedPage =
    routeAndTeamInfo?.projectId != null || routeAndTeamInfo?.environmentId != null;
  return onScopedPage ? undefined : false;
}
